import classnames from 'classnames';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Controller } from 'react-hook-form';

// app
import { FormText } from 'components';
import config from 'config';
import * as utils from 'utils';
import styles from './FormDate.styles';

// mui
import { makeStyles } from '@material-ui/core';
import EventIcon from '@material-ui/icons/Event';
import TimerIcon from '@material-ui/icons/Timer';
import TodayIcon from '@material-ui/icons/Today';
import { DatePicker, KeyboardDatePicker, KeyboardDateTimePicker, KeyboardTimePicker } from '@material-ui/pickers';

FormDateView.propTypes = {
  control: PropTypes.object,
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['date', 'datepicker']).isRequired,
  label: PropTypes.string,
  hint: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  outputFormat: PropTypes.string,
  plainText: PropTypes.bool,
  muiComponentProps: PropTypes.object,
  muiPickerProps: PropTypes.object,
  handleUpdate: PropTypes.func,
  nestedClasses: PropTypes.object,
  error: PropTypes.object,
  isDatetimePicker: PropTypes.bool,
};

FormDateView.defaultProps = {
  placeholder: true,
  muiComponentProps: {},
  muiPickerProps: {},
  nestedClasses: {},
};

export function FormDateView({
  control,
  label,
  hint,
  error,
  icon,
  placeholder,
  outputFormat, // TODO: remove when ISO is the default
  inputFormat,
  plainText,
  muiComponentProps,
  muiPickerProps,
  nestedClasses,
  type,
  name,
  rules,
  defaultValue,
  handleUpdate,
  disabled = false,
  value = null,
  validate = true,
  isDatetimePicker,
}) {
  const classes = makeStyles(styles, { name: 'FormDate' })();
  const [isValidDate, setIsValidDate] = useState(true);

  const helperTextProps = {
    error: Boolean(error),
    helperText: (error && error.message) || (plainText ? undefined : hint),
  };

  if (type === 'datepicker') {
    const format = inputFormat || muiPickerProps.format || config.ui.format.date.numeric;
    const dateValue = value ? utils.string.t('format.date', { value: { date: value, format } }) : '';
    const inputVariant = plainText ? undefined : 'outlined';
    const pickerVariant = muiPickerProps.variant || 'dialog';

    const pickerProps = {
      ...omit(muiPickerProps, ['clearable', 'showTodayButton']),
      ...(pickerVariant === 'dialog' && { clearable: muiPickerProps.clearable }),
      ...(pickerVariant === 'dialog' && { showTodayButton: muiPickerProps.showTodayButton }),
    };

    helperTextProps.error = isValidDate ? Boolean(error) : true;
    helperTextProps.helperText = isValidDate
      ? (error && error.message) || (plainText ? undefined : hint)
      : muiPickerProps.format !== 'MM YYYY'
      ? utils.string.t('app.invalidDate')
      : utils.string.t('app.invalidDateMonth');

    const calendarProps = {
      label: plainText ? undefined : label,
      emptyLabel: typeof placeholder === 'string' ? placeholder : placeholder ? utils.string.t('app.inputDate') : undefined,
      autoOk: true,
      disabled: disabled,
      id: name,
      inputVariant: inputVariant,
      ...muiComponentProps,
      ...pickerProps,
      ...helperTextProps,
      format: format,
      margin: muiComponentProps.margin || 'normal',
      inputProps: {
        'data-form-type': type,
      },
      InputProps: {
        'data-value': dateValue,
        classes: {
          root: classnames({
            [classes.dateInputWrapper]: plainText,
            [classes.adornedEnd]: !Boolean(plainText),
            [nestedClasses.root]: plainText && Boolean(nestedClasses.root),
          }),
          input: classnames({
            [classes.dateInput]: plainText,
            [nestedClasses.input]: plainText && Boolean(nestedClasses.input),
          }),
        },
      },
    };

    return (
      <Controller
        control={control}
        defaultValue={defaultValue}
        render={({ field: { onChange, name, value } }) => {
          if (isDatetimePicker) {
            return (
              <KeyboardDateTimePicker
                value={value}
                onChange={(value) => {
                  onChange(value.format());
                }}
                {...calendarProps}
                format={inputFormat ?? 'DD-MM-yyyy hh:mm A'}
                keyboardIcon={icon ? <TodayIcon /> : <EventIcon />}
              />
            );
          }

          return plainText ? (
            <DatePicker
              value={value}
              onChange={(value) => {
                onChange(value.format());
              }}
              {...calendarProps}
            />
          ) : (
            <KeyboardDatePicker
              name={name}
              value={typeof value === 'string' ? value.slice(0, 10) : value || null}
              onChange={(value) => {
                let date;
                // TODO added on 06/03/2020: make ISO the default when all endpoints support this format
                if (outputFormat === 'iso') {
                  date = value?.isValid() ? value.format('YYYY-MM-DDT00:00:00.000') : value;
                } else {
                  date = value?.format('YYYY-MM-DD') ?? null;
                }

                if (utils.generic.isFunction(handleUpdate)) {
                  handleUpdate(name, date);
                }

                if (validate) {
                  if (value?.isValid()) setIsValidDate(true);
                  else setIsValidDate(false);
                }
                onChange(date);
              }}
              {...calendarProps}
              keyboardIcon={icon ? <TodayIcon /> : <EventIcon />}
            />
          );
        }}
        id={name}
        rules={rules}
        name={name}
      />
    );
  }

  if (type === 'timepicker') {
    const timerProps = {
      label: label,
      emptyLabel: typeof placeholder === 'string' ? placeholder : placeholder ? utils.string.t('app.inputTime') : undefined,
      autoOk: true,
      disabled: disabled,
      inputVariant: 'outlined',
      id: name,
      ...muiComponentProps,
      ...helperTextProps,
      margin: muiComponentProps.margin || 'normal',
    };

    return (
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ field: { onChange, name, value } }) => (
          <KeyboardTimePicker
            name={name}
            value={value}
            onChange={onChange}
            {...timerProps}
            ampm={false}
            keyboardIcon={<TimerIcon />}
            KeyboardButtonProps={{
              'aria-label': 'change time',
            }}
          />
        )}
      />
    );
  }

  return (
    <FormText
      control={control}
      type="date"
      id={name}
      name={name}
      label={label}
      hint={hint}
      error={error}
      muiComponentProps={{
        ...muiComponentProps,
        InputLabelProps: {
          shrink: true,
          classes: {
            root: classes.label,
          },
        },
      }}
    />
  );
}
